// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="Sidebar" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Sheet, SheetContent, SheetTitle } from '../molecules/sheet';

// ─── SidebarHeader ────────────────────────────────────────────────────────────

export interface SidebarHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex h-16 shrink-0 items-center border-b border-border px-4', className)}
      {...props}
    />
  ),
);
SidebarHeader.displayName = 'SidebarHeader';

// ─── SidebarContent ───────────────────────────────────────────────────────────

export interface SidebarContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidebarContent = React.forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex-1 overflow-y-auto py-4', className)} {...props} />
  ),
);
SidebarContent.displayName = 'SidebarContent';

// ─── SidebarFooter ────────────────────────────────────────────────────────────

export interface SidebarFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('shrink-0 border-t border-border p-4', className)} {...props} />
  ),
);
SidebarFooter.displayName = 'SidebarFooter';

// ─── SidebarNav ───────────────────────────────────────────────────────────────

export interface SidebarNavProps extends React.HTMLAttributes<HTMLElement> {}

const SidebarNav = React.forwardRef<HTMLElement, SidebarNavProps>(
  ({ className, ...props }, ref) => (
    <nav ref={ref} className={cn('flex flex-col gap-1 px-2', className)} {...props} />
  ),
);
SidebarNav.displayName = 'SidebarNav';

// ─── SidebarNavItem ───────────────────────────────────────────────────────────

export interface SidebarNavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  icon?: React.ReactNode;
  collapsed?: boolean;
}

const SidebarNavItem = React.forwardRef<HTMLAnchorElement, SidebarNavItemProps>(
  ({ className, active, icon, collapsed, children, ...props }, ref) => (
    <a
      ref={ref}
      className={cn(
        'flex items-center gap-3 rounded-[var(--radius)] px-3 py-2 text-sm font-medium transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
        collapsed && 'justify-center px-2',
        className,
      )}
      {...props}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      {!collapsed && children}
    </a>
  ),
);
SidebarNavItem.displayName = 'SidebarNavItem';

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  side?: 'left' | 'right';
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      className,
      collapsed = false,
      side = 'left',
      mobileOpen = false,
      onMobileOpenChange,
      children,
      ...props
    },
    ref,
  ) => (
    <>
      {/* Desktop sidebar */}
      <div
        ref={ref}
        className={cn(
          'hidden h-full flex-col bg-background transition-[width] duration-300 lg:flex',
          side === 'left' ? 'border-r border-border' : 'border-l border-border',
          collapsed ? 'w-16' : 'w-64',
          className,
        )}
        {...props}
      >
        {children}
      </div>

      {/* Mobile sidebar via Sheet */}
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side={side === 'left' ? 'left' : 'right'}
          className="p-0"
          aria-describedby={undefined}
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex h-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    </>
  ),
);
Sidebar.displayName = 'Sidebar';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarNav, SidebarNavItem };
